/**
 * @module hooks/useSessionState
 * @description
 *   Server session sync, billing counters, and subscription recovery for the home page.
 *
 * @extracted-from client/src/App.js (lines ~160–596, Phase 7)
 *
 * @invariants
 *   - GET /api/session-state field names unchanged
 *   - localStorage keys: criticai_submission_count, criticai_paid_analyses, etc.
 *   - Math.max(client, server) for submission count and paidAnalyses (cookie-loss safe)
 *
 * @dependencies
 *   - api/client, utils/analytics
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/client';
import analytics from '../utils/analytics';

export function useSessionState() {
  const [submissionCount, setSubmissionCount] = useState(0);
  const [paidAnalyses, setPaidAnalyses] = useState(0);
  const [lastDailyAnalysis, setLastDailyAnalysis] = useState(null);
  const [dailyAnalysesAvailable, setDailyAnalysesAvailable] = useState(0);
  /** From GET /api/session-state — used for subscription checkout (email + stable id). */
  const [sessionUser, setSessionUser] = useState(null);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [subscriptionAnalysesUsed, setSubscriptionAnalysesUsed] = useState(0);
  const [subscriptionAnalysesLimit, setSubscriptionAnalysesLimit] = useState(50);
  const [paidAnalysesHighWater, setPaidAnalysesHighWater] = useState(0);
  const previousSubscriptionActiveRef = useRef(false);

  // Sync session state with server. Returns a snapshot for immediate gates (submit, etc.).
  const syncSessionState = useCallback(async () => {
    const readLocalSnapshot = () => {
      const count = parseInt(localStorage.getItem('criticai_submission_count') || '0', 10);
      const paid = parseInt(localStorage.getItem('criticai_paid_analyses') || '0', 10);
      const lastDaily = localStorage.getItem('criticai_last_daily_analysis');
      const dailyAvailable = parseInt(localStorage.getItem('criticai_daily_analyses_available') || '0', 10);
      const hasSubFlag =
        localStorage.getItem('criticai_subscription') === 'true' ||
        !!localStorage.getItem('criticai_subscription_id') ||
        !!localStorage.getItem('criticai_subscription_customer_id');
      return {
        submissionCount: count,
        paidAnalyses: paid,
        paidAnalysesHighWater: parseInt(localStorage.getItem('criticai_paid_analyses_high_water') || String(paid), 10),
        lastDailyAnalysis: lastDaily,
        dailyAnalysesAvailable: dailyAvailable,
        hasActiveSubscription: hasSubFlag,
        subscriptionAnalysesUsed: 0,
        subscriptionAnalysesLimit: 50,
      };
    };

    try {
      const response = await api.get('/api/session-state');
      if (response.data.user) {
        setSessionUser(response.data.user);
      } else {
        setSessionUser(null);
      }
      const serverCount = response.data.submissionCount || 0;
      const serverPaid =
        typeof response.data.paidAnalyses === 'number' ? response.data.paidAnalyses : null;
      const serverLastDaily = response.data.lastDailyAnalysis;
      const serverDailyAvailable = response.data.dailyAnalysesAvailable || 0;

      // Get current localStorage values
      const currentCount = parseInt(localStorage.getItem('criticai_submission_count') || '0', 10);
      const currentPaid = parseInt(localStorage.getItem('criticai_paid_analyses') || '0', 10);

      // Use the higher submission count to prevent users from bypassing free-tier limits
      const finalCount = Math.max(currentCount, serverCount);
      // Signed-in: trust server (Firestore) paid balance so devices stay in sync.
      // Anonymous: Math.max so a lost cookie cannot wipe a fresh Success-page write.
      const finalPaid =
        response.data.user && typeof serverPaid === 'number'
          ? serverPaid
          : Math.max(currentPaid, serverPaid ?? 0);

      setSubmissionCount(finalCount);
      setPaidAnalyses(finalPaid);
      const serverHw =
        typeof response.data.paidAnalysesHighWater === 'number'
          ? response.data.paidAnalysesHighWater
          : finalPaid;
      const finalHw = Math.max(serverHw, finalPaid);
      setPaidAnalysesHighWater(finalHw);
      setLastDailyAnalysis(serverLastDaily);
      setDailyAnalysesAvailable(serverDailyAvailable);

      // Check for active subscription
      let subscriptionActive = false;
      let analysesUsed = 0;
      let analysesLimit = 50;
      if (
        response.data.subscription &&
        (response.data.subscription.status === 'active' ||
          response.data.subscription.status === 'trialing')
      ) {
        subscriptionActive = true;
        analysesUsed = response.data.subscription.analysesUsed || 0;
        analysesLimit = response.data.subscription.analysesLimit || 50;
        setHasActiveSubscription(true);
        setSubscriptionAnalysesUsed(analysesUsed);
        setSubscriptionAnalysesLimit(analysesLimit);
        // Store subscription info in localStorage for recovery
        if (response.data.subscription.subscriptionId) {
          localStorage.setItem('criticai_subscription_id', response.data.subscription.subscriptionId);
        }
        if (response.data.subscription.customerId) {
          localStorage.setItem('criticai_subscription_customer_id', response.data.subscription.customerId);
        }
        if (response.data.subscription.currentPeriodEnd) {
          localStorage.setItem('criticai_subscription_period_end', response.data.subscription.currentPeriodEnd);
        }
        localStorage.setItem('criticai_subscription', 'true');
        console.log('✅ Active subscription detected:', {
          subscriptionId: response.data.subscription.subscriptionId,
          customerId: response.data.subscription.customerId,
          analysesLimit,
          analysesUsed,
          remaining: analysesLimit - analysesUsed,
        });
      } else {
        // If server doesn't have subscription, check localStorage and try to recover
        const storedSubscriptionId = localStorage.getItem('criticai_subscription_id');
        const storedCustomerId = localStorage.getItem('criticai_subscription_customer_id');

        if (storedSubscriptionId || storedCustomerId) {
          // Try to recover subscription status from server
          try {
            const recoveryResponse = await api.post('/api/recover-subscription', {
              subscriptionId: storedSubscriptionId,
              customerId: storedCustomerId,
            });
            if (recoveryResponse.data.subscription && recoveryResponse.data.subscription.status === 'active') {
              subscriptionActive = true;
              analysesUsed = recoveryResponse.data.subscription.analysesUsed || 0;
              analysesLimit = recoveryResponse.data.subscription.analysesLimit || 50;
              setHasActiveSubscription(true);
              setSubscriptionAnalysesUsed(analysesUsed);
              setSubscriptionAnalysesLimit(analysesLimit);
              if (recoveryResponse.data.subscription.subscriptionId) {
                localStorage.setItem('criticai_subscription_id', recoveryResponse.data.subscription.subscriptionId);
              }
              if (recoveryResponse.data.subscription.customerId) {
                localStorage.setItem('criticai_subscription_customer_id', recoveryResponse.data.subscription.customerId);
              }
              if (recoveryResponse.data.subscription.currentPeriodEnd) {
                localStorage.setItem('criticai_subscription_period_end', recoveryResponse.data.subscription.currentPeriodEnd);
              }
              localStorage.setItem('criticai_subscription', 'true');
            } else {
              // Only clear local flags when server confirms inactive (not on cookie/session loss)
              setHasActiveSubscription(false);
              setSubscriptionAnalysesUsed(0);
              setSubscriptionAnalysesLimit(50);
              localStorage.removeItem('criticai_subscription_id');
              localStorage.removeItem('criticai_subscription_customer_id');
              localStorage.removeItem('criticai_subscription_period_end');
              localStorage.removeItem('criticai_subscription');
            }
          } catch (recoveryError) {
            console.error('Failed to recover subscription:', recoveryError);
            // Keep optimistic local subscription state when recovery fails (often cookie loss)
            const keepLocal =
              localStorage.getItem('criticai_subscription') === 'true' ||
              !!localStorage.getItem('criticai_subscription_id');
            subscriptionActive = keepLocal;
            setHasActiveSubscription(keepLocal);
            if (!keepLocal) {
              setSubscriptionAnalysesUsed(0);
              setSubscriptionAnalysesLimit(50);
            }
          }
        } else {
          // No subscription data in localStorage, but check if user might have a subscription
          // by checking if they have a subscription flag set
          const hasSubscriptionFlag = localStorage.getItem('criticai_subscription') === 'true';
          if (hasSubscriptionFlag && response.data.user && response.data.user.email) {
            // User has subscription flag but lost IDs, try to recover via email
            try {
              const emailRecoveryResponse = await api.post('/api/recover-subscription', {
                email: response.data.user.email,
              });

              if (emailRecoveryResponse.data.subscription && emailRecoveryResponse.data.subscription.status === 'active') {
                subscriptionActive = true;
                analysesUsed = emailRecoveryResponse.data.subscription.analysesUsed || 0;
                analysesLimit = emailRecoveryResponse.data.subscription.analysesLimit || 50;
                setHasActiveSubscription(true);
                setSubscriptionAnalysesUsed(analysesUsed);
                setSubscriptionAnalysesLimit(analysesLimit);
                if (emailRecoveryResponse.data.subscription.subscriptionId) {
                  localStorage.setItem('criticai_subscription_id', emailRecoveryResponse.data.subscription.subscriptionId);
                }
                if (emailRecoveryResponse.data.subscription.customerId) {
                  localStorage.setItem('criticai_subscription_customer_id', emailRecoveryResponse.data.subscription.customerId);
                }
                if (emailRecoveryResponse.data.subscription.currentPeriodEnd) {
                  localStorage.setItem('criticai_subscription_period_end', emailRecoveryResponse.data.subscription.currentPeriodEnd);
                }
                localStorage.setItem('criticai_subscription', 'true');
              }
            } catch (emailRecoveryError) {
              console.error('Failed to recover subscription by email:', emailRecoveryError);
            }
          }

          if (!subscriptionActive) {
            setHasActiveSubscription(false);
            setSubscriptionAnalysesUsed(0);
            setSubscriptionAnalysesLimit(50);
          }
        }
      }

      localStorage.setItem('criticai_submission_count', finalCount.toString());
      localStorage.setItem('criticai_paid_analyses', finalPaid.toString());
      localStorage.setItem('criticai_paid_analyses_high_water', finalHw.toString());
      if (serverLastDaily) {
        localStorage.setItem('criticai_last_daily_analysis', serverLastDaily);
      }
      localStorage.setItem('criticai_daily_analyses_available', serverDailyAvailable.toString());

      console.log('Session state synced successfully:', {
        serverCount,
        serverPaid,
        serverDailyAvailable,
        finalCount,
        finalPaid,
        finalHw,
        serverLastDaily,
      });

      return {
        submissionCount: finalCount,
        paidAnalyses: finalPaid,
        paidAnalysesHighWater: finalHw,
        lastDailyAnalysis: serverLastDaily,
        dailyAnalysesAvailable: serverDailyAvailable,
        hasActiveSubscription: subscriptionActive,
        subscriptionAnalysesUsed: analysesUsed,
        subscriptionAnalysesLimit: analysesLimit,
      };
    } catch (error) {
      console.error('Failed to sync session state:', error);

      // Enhanced fallback behavior — prefer localStorage over wiping credits
      const snapshot = readLocalSnapshot();
      setSubmissionCount(snapshot.submissionCount);
      setPaidAnalyses(snapshot.paidAnalyses);
      setPaidAnalysesHighWater(
        Math.max(snapshot.paidAnalysesHighWater || 0, snapshot.paidAnalyses || 0)
      );
      setLastDailyAnalysis(snapshot.lastDailyAnalysis);
      setDailyAnalysesAvailable(snapshot.dailyAnalysesAvailable);

      // Try to recover subscription status from localStorage and verify with server
      const storedSubscriptionId = localStorage.getItem('criticai_subscription_id');
      const storedCustomerId = localStorage.getItem('criticai_subscription_customer_id');
      let subscriptionActive = snapshot.hasActiveSubscription;
      let analysesUsed = 0;
      let analysesLimit = 50;

      if (storedSubscriptionId || storedCustomerId) {
        try {
          const recoveryResponse = await api.post('/api/recover-subscription', {
            subscriptionId: storedSubscriptionId,
            customerId: storedCustomerId,
          });

          if (recoveryResponse.data.subscription && recoveryResponse.data.subscription.status === 'active') {
            subscriptionActive = true;
            analysesUsed = recoveryResponse.data.subscription.analysesUsed || 0;
            analysesLimit = recoveryResponse.data.subscription.analysesLimit || 50;
            setHasActiveSubscription(true);
            setSubscriptionAnalysesUsed(analysesUsed);
            setSubscriptionAnalysesLimit(analysesLimit);
            if (recoveryResponse.data.subscription.subscriptionId) {
              localStorage.setItem('criticai_subscription_id', recoveryResponse.data.subscription.subscriptionId);
            }
            if (recoveryResponse.data.subscription.customerId) {
              localStorage.setItem('criticai_subscription_customer_id', recoveryResponse.data.subscription.customerId);
            }
            if (recoveryResponse.data.subscription.currentPeriodEnd) {
              localStorage.setItem('criticai_subscription_period_end', recoveryResponse.data.subscription.currentPeriodEnd);
            }
            localStorage.setItem('criticai_subscription', 'true');
          } else {
            setHasActiveSubscription(false);
            setSubscriptionAnalysesUsed(0);
            setSubscriptionAnalysesLimit(50);
            localStorage.removeItem('criticai_subscription_id');
            localStorage.removeItem('criticai_subscription_customer_id');
            localStorage.removeItem('criticai_subscription_period_end');
            localStorage.removeItem('criticai_subscription');
            subscriptionActive = false;
          }
        } catch (recoveryError) {
          console.error('Failed to recover subscription:', recoveryError);
          // Keep local optimistic state when network/cookie recovery fails
          setHasActiveSubscription(subscriptionActive);
          if (!subscriptionActive) {
            setSubscriptionAnalysesUsed(0);
            setSubscriptionAnalysesLimit(50);
          }
        }
      } else {
        setHasActiveSubscription(false);
        setSubscriptionAnalysesUsed(0);
        setSubscriptionAnalysesLimit(50);
        subscriptionActive = false;
      }

      console.log('Using fallback session state:', {
        count: snapshot.submissionCount,
        paid: snapshot.paidAnalyses,
        lastDaily: snapshot.lastDailyAnalysis,
        dailyAvailable: snapshot.dailyAnalysesAvailable,
      });

      return {
        ...snapshot,
        hasActiveSubscription: subscriptionActive,
        subscriptionAnalysesUsed: analysesUsed,
        subscriptionAnalysesLimit: analysesLimit,
      };
    }
  }, []);

  // Sync session state on mount to detect subscription
  useEffect(() => {
    // Wrap in try-catch to prevent crashes
    try {
      syncSessionState().catch((error) => {
        console.error('Error in syncSessionState on mount:', error);
        // Don't crash the app, just log the error
      });
    } catch (error) {
      console.error('Error calling syncSessionState:', error);
    }
  }, [syncSessionState]);

  useEffect(() => {
    if (previousSubscriptionActiveRef.current && !hasActiveSubscription) {
      analytics.trackSubscriptionCancelled('session_state_change');
    }
    previousSubscriptionActiveRef.current = hasActiveSubscription;
  }, [hasActiveSubscription]);

  // Check for return visits and load submission count
  useEffect(() => {
    const hasVisited = localStorage.getItem('criticai_visited');
    if (hasVisited) {
      analytics.trackReturnVisit();
    } else {
      localStorage.setItem('criticai_visited', 'true');
    }

    // Load submission count and paid analyses from localStorage
    const count = parseInt(localStorage.getItem('criticai_submission_count') || '0');
    const paid = parseInt(localStorage.getItem('criticai_paid_analyses') || '0');
    const lastDaily = localStorage.getItem('criticai_last_daily_analysis');
    const dailyAvailable = parseInt(localStorage.getItem('criticai_daily_analyses_available') || '0');

    setSubmissionCount(count);
    setPaidAnalyses(paid);
    const storedHw = parseInt(
      localStorage.getItem('criticai_paid_analyses_high_water') || String(paid),
      10
    );
    setPaidAnalysesHighWater(Math.max(storedHw || 0, paid || 0));
    setLastDailyAnalysis(lastDaily);
    setDailyAnalysesAvailable(dailyAvailable);

    // Initialize subscription state from localStorage (will be verified/updated by syncSessionState)
    const hasSubscriptionFlag = localStorage.getItem('criticai_subscription') === 'true';
    const storedSubscriptionId = localStorage.getItem('criticai_subscription_id');
    const storedCustomerId = localStorage.getItem('criticai_subscription_customer_id');

    // If subscription data exists in localStorage, set initial state optimistically
    // This ensures correct display on desktop before async sync completes
    if (hasSubscriptionFlag || storedSubscriptionId || storedCustomerId) {
      // Set optimistic subscription state - will be verified/updated by syncSessionState
      setHasActiveSubscription(true);
      // Note: analysesUsed and limit will be set by syncSessionState, but set defaults for immediate display
      setSubscriptionAnalysesUsed(0); // Will be updated by sync
      setSubscriptionAnalysesLimit(50); // Default limit
    } else {
      setHasActiveSubscription(false);
      setSubscriptionAnalysesUsed(0);
      setSubscriptionAnalysesLimit(50);
    }

    // Sync with server session state (will update subscription state with actual values)
    syncSessionState();
  }, [syncSessionState]);

  return {
    submissionCount,
    setSubmissionCount,
    paidAnalyses,
    setPaidAnalyses,
    paidAnalysesHighWater,
    setPaidAnalysesHighWater,
    lastDailyAnalysis,
    setLastDailyAnalysis,
    dailyAnalysesAvailable,
    setDailyAnalysesAvailable,
    sessionUser,
    hasActiveSubscription,
    setHasActiveSubscription,
    subscriptionAnalysesUsed,
    setSubscriptionAnalysesUsed,
    subscriptionAnalysesLimit,
    setSubscriptionAnalysesLimit,
    syncSessionState,
  };
}

export default useSessionState;
